import type { ContainerRect } from './face-centering';
import { isPositiveSize } from './geometry-guards';

export interface FitRectOptions {
  readonly mode: 'cover' | 'contain';
  readonly round: boolean;
}

export interface FitRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** Единственная реализация cover/contain-подгонки фото под контейнер. Годность контейнера — забота вызывающего. */
export function fitRect(imageWidth: number, imageHeight: number, container: ContainerRect, options: FitRectOptions): FitRect {
  if (!isPositiveSize(imageWidth, imageHeight)) {
    return { left: 0, top: 0, width: container.width, height: container.height };
  }

  const imageRatio = imageWidth / imageHeight;

  let width = container.width;
  let height = container.width / imageRatio;

  // cover добирает недостающую высоту, contain — срезает лишнюю.
  const fitBySecondAxis = options.mode === 'contain' ? height > container.height : height < container.height;
  if (fitBySecondAxis) {
    height = container.height;
    width = container.height * imageRatio;
  }

  if (options.round) {
    width = Math.round(width);
    height = Math.round(height);
  }

  const left = (container.width - width) / 2;
  const top = (container.height - height) / 2;

  return options.round ? { left: Math.round(left), top: Math.round(top), width, height } : { left, top, width, height };
}

/**
 * Кроп и зум меняют размер прямоугольника, но не отношение сторон. Допуск — на писателей, которые тянут
 * окно по осям порознь (проверено грепом по `scaleDrawableGeometry` и `refitImageBox`): рефит
 * JPEG-обложки при смене корешка (`refit-jpeg-cover.ts`, обе формы) и сборка обложки чужого формата
 * (`build-cover.ts`) — там, где `keepAspectAfterFit` пропорцию не возвращает: кусок навылет к краю листа
 * и кусок, которому раскладка сдвинула ширину. Остальные вызовы либо чистый перенос, либо проходят
 * `keepRasterAspect` и пропорцию сохраняют.
 */
const PLACEMENT_ASPECT_TOLERANCE = 0.02;

export interface PlacementRectLike {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly angle?: number;
}

/**
 * Кадр фото в рамке — ЕДИНСТВЕННОЕ место, где персист-прямоугольник признаётся годным.
 *
 * Персист годен только при родной пропорции фото: прямоугольник, снятый с РАМКИ (кадра не было, декод
 * подставлял рамку — и энкод писал её обратно), растягивает фото под пропорцию рамки. Такой кадр и пустой
 * кадр одинаково пересчитываются cover-подгонкой по родному размеру. Живой cover-кадр без поворота при
 * чужой пропорции сохраняет зум и центр (`refitAtNativeAspect`); contain и повёрнутый кадр — сброс.
 *
 * СЛОМАННОЕ окно (нечисловая или неположительная сторона) отдаётся дословно: чинить его здесь значит
 * ослепить гейты целостности, которые ищут ровно такую геометрию и решают судьбу разворота
 * (`hasFiniteImageGeometry` и его боксовый твин в book-generator.service). Пропорцию судим только у
 * годного прямоугольника.
 */
export function photoPlacementRect(
  placement: PlacementRectLike | null | undefined,
  nativeWidth: number | undefined,
  nativeHeight: number | undefined,
  container: ContainerRect,
  mode: 'cover' | 'contain' = 'cover',
): { left: number; top: number; width: number; height: number; angle: number } {
  const angle = placement?.angle ?? 0;
  // Родной размер необязателен в персисте: без него нечем судить пропорцию, и fitRect сам отдаёт рамку.
  const native = { width: nativeWidth ?? 0, height: nativeHeight ?? 0 };
  const nativeRatio = isPositiveSize(native.width, native.height) ? native.width / native.height : undefined;

  if (placement) {
    const verbatim = { left: placement.left, top: placement.top, width: placement.width, height: placement.height, angle };
    if (!isPositiveSize(placement.width, placement.height)) return verbatim;

    const ratio = placement.width / placement.height;
    if (nativeRatio === undefined || Math.abs(ratio - nativeRatio) <= nativeRatio * PLACEMENT_ASPECT_TOLERANCE) {
      return verbatim;
    }

    // Кадр, снятый с рамки, — это зум 1 по центру: перенос даёт ровно центрированный cover, отдельной ветки не надо.
    // Нулевая рамка (легаси-геометрия null → [x,y,0,0]) даёт зум Infinity и NaN-кадр — её ведёт fitRect.
    if (mode === 'cover' && angle === 0 && isPositiveSize(container.width, container.height)) {
      return { ...refitAtNativeAspect(placement, native, container), angle };
    }
  }

  return { ...fitRect(native.width, native.height, container, { mode, round: true }), angle };
}

/**
 * Фото сменило родную пропорцию (перезалив, EXIF-поворот) под живым кадром: зум и центр кадра — выбор
 * фотографа, их переносим на родную пропорцию, затем кадр дорастает и сдвигается, чтобы закрыть рамку.
 * Округление то же, что у `fitRect(round: true)`, чтобы кадр с рамки совпадал со сбросом до пикселя.
 */
function refitAtNativeAspect(
  rect: PlacementRectLike,
  native: { width: number; height: number },
  container: ContainerRect,
): FitRect {
  const cover = { mode: 'cover', round: false } as const;
  const zoom = rect.width / fitRect(rect.width, rect.height, container, cover).width;
  const base = fitRect(native.width, native.height, container, cover);
  const grow = Math.max(1, container.width / (base.width * zoom), container.height / (base.height * zoom));
  const width = Math.round(base.width * zoom * grow);
  const height = Math.round(base.height * zoom * grow);
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const clamp = (value: number, min: number): number => Math.min(0, Math.max(min, value));
  return {
    left: Math.round(clamp(centerX - width / 2, container.width - width)),
    top: Math.round(clamp(centerY - height / 2, container.height - height)),
    width,
    height,
  };
}
